import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { SettingFilled } from '@ant-design/icons';
import { iconAndAltForIntegrationType } from '../helpers/integrationHelpers';
import { IntegrationCapability } from '../types';
import { antGrey, colorWithAlpha, runwayColors, rwColors, tagStyle } from './styles/globalStyles';
import { allSupportedIntegrationTypesForCapability, displayNameForCapability } from '../helpers';
import { capabilityNameWithPrefix, documentationLinkForCapability } from '../helpers/displayHelpers';
import { useSelector } from 'react-redux';
import { selectIsRunwayOrg } from '../selectors/selectors';
import { RootState } from '../reducers/reducers';
import Button from 'antd/lib/button';
import List from 'antd/lib/list';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import Tag from 'antd/lib/tag';
import Link from 'antd/lib/typography/Link';
import Title from 'antd/lib/typography/Title';
import { createSetupRoute } from '../helpers/routeHelpers';
import {
    AppComponentProps,
    CALLER_CONTEXT_PARAM,
    CAPABILITY_URL_PARAM,
    REDIRECT_URL_PARAM,
    SetupStepsCallerContext,
} from '../typescriptHelpers';

interface ConnectIntegrationsBannerProps {
    children: React.ReactNode;
}
const ConnectIntegrationsBannerWrapper: React.FC<ConnectIntegrationsBannerProps> = (
    props: ConnectIntegrationsBannerProps,
) => {
    const { children } = props;

    return (
        <div
            style={{
                backgroundColor: colorWithAlpha(rwColors.primary, 0.05),
                borderRadius: 8,
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 12,
                paddingBottom: 12,
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden' }}>{children}</div>
        </div>
    );
};

interface ConfigureIntegrationsButtonProps {
    route: string;
}
const ConfigureIntegrationsButton: React.FC<ConfigureIntegrationsButtonProps> = (
    props: ConfigureIntegrationsButtonProps,
) => {
    const { route } = props;
    const history = useHistory();
    return (
        <Button
            type="primary"
            onClick={() => {
                history.push(route);
            }}
            icon={<SettingFilled />}
        >
            Configure integrations
        </Button>
    );
};

interface CapabilityIntegrationsListItemProps extends AppComponentProps {
    capability: IntegrationCapability;
    description: string;
    docsNodeForCapability: React.ReactNode;
    onClickConnect?: (defaultRoute: string) => void;
}
const CapabilityIntegrationsListItem: React.FC<CapabilityIntegrationsListItemProps> = (
    props: CapabilityIntegrationsListItemProps,
) => {
    const { capability, description, docsNodeForCapability, onClickConnect } = props;
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));
    const integrations = allSupportedIntegrationTypesForCapability(capability, isRunwayOrg, props.orgId);

    const history = useHistory();

    return (
        <List.Item style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 16, paddingBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 500, minWidth: 200 }}>
                    <Title level={5}>Connect {capabilityNameWithPrefix(capability)} integration</Title>
                    <Text>{description}</Text>
                    <Space split={<Divider type="vertical" />} style={{ marginTop: 12 }}>
                        <Button
                            type="link"
                            style={{ padding: 0, fontWeight: 600 }}
                            onClick={() => {
                                // get current url without origin
                                const currentUrl = window.location.pathname + window.location.search;

                                const encodedCurrentUrl = encodeURIComponent(currentUrl);

                                const route = `${createSetupRoute(
                                    props.orgId,
                                    props.appId,
                                )}?${REDIRECT_URL_PARAM}=${encodedCurrentUrl}`;

                                if (onClickConnect) {
                                    onClickConnect(route);
                                } else {
                                    const capabilityRoute = `${route}&${CAPABILITY_URL_PARAM}=${capability}&${CALLER_CONTEXT_PARAM}=${SetupStepsCallerContext.CommonIntegrationCapability}`;
                                    history.push(capabilityRoute);
                                }
                            }}
                        >
                            Connect now
                        </Button>
                        {docsNodeForCapability}
                    </Space>
                </div>
                <div
                    style={{
                        backgroundColor: runwayColors.white,
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: 12,
                        marginLeft: 24,
                    }}
                >
                    <div>
                        {integrations.map((integrationType) => {
                            const [icon, alt] = iconAndAltForIntegrationType(integrationType);

                            return (
                                <img
                                    key={integrationType}
                                    src={icon}
                                    alt={alt}
                                    style={{ marginRight: 8 }}
                                    width={30}
                                    height={30}
                                />
                            );
                        })}
                    </div>
                    <Tag color={antGrey.grey6} style={tagStyle}>
                        Not connected
                    </Tag>
                </div>
            </div>
        </List.Item>
    );
};

interface DefaultDocsLinkNodeProps {
    capability: IntegrationCapability;
}
const DefaultDocsLinkNode: React.FC<DefaultDocsLinkNodeProps> = (props: DefaultDocsLinkNodeProps) => {
    const { capability } = props;
    const name = displayNameForCapability(capability);

    return (
        <Link href={documentationLinkForCapability(capability)} target="_blank">
            {name} docs
        </Link>
    );
};

export {
    ConnectIntegrationsBannerWrapper,
    CapabilityIntegrationsListItem,
    ConfigureIntegrationsButton,
    DefaultDocsLinkNode,
};
